import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import type { FunctionComponent } from "react";
import { useEffect, useRef, useState } from "react";
import { CenteredFlexRow, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { Button } from "@similarweb/ui-components";
import { DropdownReact } from "app/components/dropdown/dropdown-react";
import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import { PlainTooltip } from "@similarweb/ui-components";
import type { IAdvertisersTableTopProps } from "app/pages/website-analysis/analyze-publishers/advertisers/common/types";
import {
    CompetitiveCompareBarsStyled,
    CompetitiveCompareBarsTitle,
    DistributionBox,
    DownloadExcelContainer,
    DropdownWrapper,
    Filters,
    FiltersGrow,
    LoaderWrapper,
    MetricTitleWithDropdownWrapper,
    NetworksFilterWrapper,
    Right,
    SearchContainer,
    Section,
    SectionWrapper,
    Separator,
    Text,
    TitleContainer,
    TitleWithDropdownWrapper,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/StyledComponents";
import type { IDistributionItemTable } from "app/pages/website-analysis/analyze-publishers/advertisers/compare/AdvertisersTableCompare";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { NetworksFilter } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/filters/NetworksFilter";
import { CategoryFilter } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/filters/CategoryFilter";
import { AdvertisersOverview } from "app/pages/website-analysis/analyze-publishers/advertisers/common/AdvertisersOverview";
import { AdvertisersDistributionByCategories } from "app/pages/website-analysis/analyze-publishers/advertisers/compare/AdvertisersDistributionByCategories";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { AdvertisersDistributionByNetworks } from "app/pages/website-analysis/analyze-publishers/advertisers/compare/AdvertisersDistributionByNetworks";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";

interface IAdvertisersTableTopCompareProps extends IAdvertisersTableTopProps {
    competitiveTrafficShare: Array<{
        color: string;
        name: string;
        percentage: number;
        value: number;
        valueText: string;
        width: number;
    }>;
    networksDistribution: IDistributionItemTable;
    categoriesDistribution: IDistributionItemTable;
    domains: { color: string; name: string }[];
    topNetworks: string[];
}

export const AdvertisersTableTopCompare: FunctionComponent<IAdvertisersTableTopCompareProps> = (props) => {
    const {
        isLoadingData,
        tableColumns,
        onClickToggleColumns,
        downloadExcelPermitted,
        excelLink,
        searchValue,
        i18n,
        category,
        network,
        onFilterChange,
        filtersData,
        totalSectionData,
        competitiveTrafficShare,
        networksDistribution,
        categoriesDistribution,
        topCategories,
        domains,
        getLink,
        topNetworks,
        chosenItems,
        title,
        tooltip,
        filtersStateObject,
        subtitleFilters,
    } = props;

    const prevCategory = useRef(category);
    const isPaidExcelPermitted = isPaidExcelAllowed();

    const distributionByItems = [
        {
            id: "DistributionByCategory",
            text: i18n("monetization.advertisers.table.top.compare.distribution.by.categories"),
            headTemp: "leader-default-header-cell",
        },
        {
            id: "DistributionByNetwork",
            text: i18n("monetization.advertisers.table.top.compare.distribution.by.networks"),
            headTemp: "leader-domain-header-cell",
        },
    ];

    const [selectedSearchBy, setSelectedSearchBy] = useState(distributionByItems[0].id);
    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const onColumnToggle = (key) => {
        if (tableColumns[key].visible) {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "remove",
                `Table Columns/${tableColumns[key].displayName || ""}`,
            );
        } else {
            allTrackers.dispatchTrackEvent("Drop Down", "add", `Table Columns/${tableColumns[key].displayName || ""}`);
        }
        onClickToggleColumns(parseInt(key, 10));
    };
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");
    };
    const onClearAll = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Table/Clear all");

        const resetParams = {
            network: null,
            industry: null,
        };
        onFilterChange(resetParams, true);
    };

    const onNetworksFilterChange = (value) => {
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Table/Network/${(value ? value.children : "Clear Filter") || ""}`,
        );
        onFilterChange({ network: value && value.children }, true);
    };

    const onCategoryClick = (category, updateUrl = false) => {
        onFilterChange({ industry: category }, updateUrl);
    };

    const onNetworkClick = (network) => {
        allTrackers.dispatchTrackEvent("Filter Table", "click", `Table/network/${network || ""}`);

        onFilterChange({ network: network }, false);
    };

    // In case a category was clicked from the table itself
    useEffect(() => {
        if (prevCategory.current !== category) {
            onCategoryClick(category);
        }
    }, [category]);

    const onCategoryFilterChange = (category) => {
        const value = category ? category.forApi.replace("~", " > ").replace(/_/g, " ") : "Clear Filter";
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Table/Category/${value || ""}`);

        onFilterChange({ industry: category && category.forApi }, true);
    };

    const renderMetricTitleWithDropdown = ({ tooltip, headline }) => {
        return (
            <MetricTitleWithDropdownWrapper>
                <CenteredFlexRow>
                    <TitleWithDropdownWrapper>
                        <Text
                            fontSize={20}
                            fontWeight={500}
                        >
                            {headline}
                        </Text>
                    </TitleWithDropdownWrapper>
                    <DropdownWrapper>
                        <DropdownReact
                            selected={selectedSearchBy}
                            onChange={onDropdownChange}
                            items={distributionByItems}
                        />
                    </DropdownWrapper>
                    <PlainTooltip
                        placement="top"
                        tooltipContent={tooltip}
                    >
                        <span>
                            <InfoIcon iconName="info" />
                        </span>
                    </PlainTooltip>
                </CenteredFlexRow>
            </MetricTitleWithDropdownWrapper>
        );
    };

    const onSearch = (value) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value || ""}`);

        onFilterChange({ search: value }, true);
    };

    const onDropdownChange = (item) => {
        setSelectedSearchBy(item.id);
    };
    return !isLoadingData ? (
        <>
            <TitleContainer>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subtitleFilters}
                />
            </TitleContainer>
            <SectionWrapper>
                <Section margin={15.5}>
                    <Filters>
                        <FlexRow>
                            <FiltersGrow>
                                {filtersData?.Networks && (
                                    <NetworksFilterWrapper>
                                        <NetworksFilter
                                            selectedNetwork={network}
                                            networks={filtersData.Networks}
                                            onNetworksFilterChange={onNetworksFilterChange}
                                        />
                                    </NetworksFilterWrapper>
                                )}
                                {filtersData?.Categories && (
                                    <CategoryFilter
                                        selectedCategory={category}
                                        onSelectCategory={onCategoryFilterChange}
                                        allCategories={filtersData.Categories}
                                    />
                                )}
                            </FiltersGrow>
                            <Button
                                type="flat"
                                isDisabled={!network && !category}
                                onClick={onClearAll}
                            >
                                {i18n("forms.buttons.clearall.text")}
                            </Button>
                        </FlexRow>
                    </Filters>
                </Section>
            </SectionWrapper>
            <Separator />
            {totalSectionData && (
                <Section margin={19}>
                    <AdvertisersOverview {...totalSectionData} />
                </Section>
            )}
            <Separator />
            {competitiveTrafficShare && competitiveTrafficShare.length > 0 && (
                <>
                    <CompetitiveCompareBarsTitle>
                        {i18n("monetization.advertisers.table.top.compare.total.competitive.traffic.share")}
                    </CompetitiveCompareBarsTitle>
                    <CompetitiveCompareBarsStyled
                        items={[{ bars: competitiveTrafficShare }]}
                        isFetching={false}
                        title={i18n("analysis.compare.trafficsource.referrals.tooltip.title")}
                    />
                </>
            )}
            <DistributionBox>
                {renderMetricTitleWithDropdown({
                    headline: i18n("monetization.advertisers.table.top.compare.distribution.by"),
                    tooltip: i18n("monetization.advertisers.table.top.compare.distribution.by.info"),
                })}
                {selectedSearchBy === distributionByItems[0].id && (
                    <AdvertisersDistributionByCategories
                        categoriesDistribution={categoriesDistribution}
                        i18n={i18n}
                        topCategories={topCategories}
                        domains={domains}
                        getLink={getLink}
                        onClick={onCategoryClick}
                    />
                )}
                {selectedSearchBy === distributionByItems[1].id && (
                    <AdvertisersDistributionByNetworks
                        networksDistribution={networksDistribution}
                        domains={domains}
                        getLink={getLink}
                        topNetworks={topNetworks}
                        onClick={onNetworkClick}
                    />
                )}
            </DistributionBox>
            <SearchContainer>
                <SearchInput
                    disableClear={true}
                    defaultValue={searchValue}
                    debounce={500}
                    onChange={onSearch}
                    placeholder={i18n("forms.search.placeholder")}
                />
                <Right>
                    <DownloadExcelContainer>
                        <DownloadButtonMenu
                            PaidExcel={isPaidExcelPermitted}
                            totalCount={totalSectionData?.totalUnGroupedCount}
                            Excel={true}
                            downloadUrl={excelDownloadUrl}
                            exportFunction={trackExcelDownload}
                            excelLocked={!downloadExcelPermitted}
                        />
                    </DownloadExcelContainer>
                    <ColumnsPickerLite
                        {...getColumnsPickerLiteProps()}
                        withTooltip
                        translations={{
                            tooltip: i18n("workspace.sales.column.picker"),
                            getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                i18n("common.columnsPicker.selectedAmount", {
                                    selected: selectedValue,
                                    total: totalValue,
                                }),
                            mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                            apply: i18n("common.apply"),
                        }}
                    />
                    <AddToDashboard
                        metric="DashboardTopOutgoingAds"
                        type="Table"
                        modelType="fromWebsite"
                        webSource={filtersStateObject.webSource}
                        filters={{}}
                        overrideAddToDashboardParams={{
                            key: chosenItems,
                        }}
                    />
                </Right>
            </SearchContainer>
        </>
    ) : (
        <LoaderWrapper>
            <WidgetLoader />
        </LoaderWrapper>
    );
};
